import React, { useState, useEffect, useCallback } from "react";
import { C } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { toast } from "react-toastify";
import { InputAdornment, MenuItem, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import {
  listPayersByFinanceClass,
  createCorporatePayer,
  listRateCards,
  setRateCardStatus,
} from "../corporate-api";
import { toBillingError } from "../billing-api";

const STATUS_TONE = {
  ACTIVE: { bg: IPD_COLORS.success.bg, color: IPD_COLORS.success.text },
  SUSPENDED: { bg: IPD_COLORS.warning.bg, color: IPD_COLORS.warning.text },
  INACTIVE: { bg: IPD_COLORS.pageBg, color: IPD_COLORS.textSecondary },
  DRAFT: { bg: "#e0e7ff", color: "#4338ca" }, // indigo — no matching hue family in IPD_COLORS, left as-is (matches PackageAdminEditor.js precedent)
};

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.INACTIVE;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 700,
        backgroundColor: tone.bg,
        color: tone.color,
      }}
    >
      {status || "—"}
    </Box>
  );
}

// ── New corporate payer modal ─────────────────────────────────────────────────
// Picks from the same EMR corporate list (payers_by_finance_class) shown in the
// "All corporates" filter, instead of free-typing a code/name.
function NewCorporateModal({ corporates, onClose, onCreated }) {
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    const picked = corporates.find((c) => String(c.id) === selectedId);
    if (!picked) {
      setError("Select a corporate");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payer = await createCorporatePayer({
        payer_code: picked.code,
        payer_name: picked.name,
        payer_type: "CORPORATE",
        is_active: true,
      });
      toast.success(`Corporate "${payer.name}" created`);
      onCreated(payer);
    } catch (err) {
      const msg = toBillingError(err, "Could not create corporate");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "15px", fontWeight: 800, color: C.text }}>
        New corporate
        <IconButton onClick={onClose} size="small">
          <SvgIcon name="x" size={18} color={C.textSub} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <IpdFormField label="Corporate">
          <IpdTextField select fullWidth value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
            <MenuItem value="">Select a corporate…</MenuItem>
            {corporates.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name} ({c.code})</MenuItem>
            ))}
          </IpdTextField>
        </IpdFormField>
        {error && <Box sx={{ fontSize: "12px", color: C.danger, fontWeight: 600 }}>{error}</Box>}
      </DialogContent>
      <DialogActions sx={{ padding: "14px 20px", gap: "10px" }}>
        <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
        <IpdButton onClick={submit} loading={saving}>Create corporate</IpdButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Rate card list ────────────────────────────────────────────────────────────
export default function CorporateRateCardList({ onOpenEditor }) {
  const [payers, setPayers] = useState([]);
  const [payerFilter, setPayerFilter] = useState(""); // "" = all
  const [cards, setCards] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNewCorporate, setShowNewCorporate] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadPayers = useCallback(async () => {
    try {
      setPayers(await listPayersByFinanceClass("CORP"));
    } catch (err) {
      // Non-fatal — rate cards can still load.
      console.error("load corporates by finance class", err);
    }
  }, []);

  const loadCards = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setCards(await listRateCards({ payerId: payerFilter || undefined }));
    } catch (err) {
      setError(toBillingError(err, "Could not load rate cards"));
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [payerFilter]);

  useEffect(() => { loadPayers(); }, [loadPayers]);
  useEffect(() => { loadCards(); }, [loadCards]);

  const toggleStatus = async (card) => {
    const next = card.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    setBusyId(card.id);
    try {
      await setRateCardStatus(card.id, next);
      toast.success(`${card.code} ${next === "ACTIVE" ? "activated" : "suspended"}`);
      await loadCards();
    } catch (err) {
      toast.error(toBillingError(err, "Could not change status"));
    } finally {
      setBusyId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? cards.filter((c) =>
        [c.code, c.name, c.payerName].filter(Boolean).join(" ").toLowerCase().includes(q)
      )
    : cards;

  const columns = [
    { field: "code", headerName: "Code", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{p.value}</Box> },
    { field: "name", headerName: "Name", flex: 1.4, minWidth: 160, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.text }}>{p.value}</Box> },
    { field: "payerName", headerName: "Corporate", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{p.value || "—"}</Box> },
    { field: "type", headerName: "Type", flex: 1, minWidth: 100, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value}</Box> },
    { field: "status", headerName: "Status", flex: 1, minWidth: 110, sortable: false,
      renderCell: (p) => <StatusBadge status={p.value} /> },
    { field: "effectiveFrom", headerName: "Effective from", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "actions", headerName: "", flex: 1.1, minWidth: 190, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IpdButton variant="outlined" size="small" onClick={() => onOpenEditor(p.row.id, null)}>
            Edit
          </IpdButton>
          <IpdButton
            variant="outlined"
            size="small"
            onClick={() => toggleStatus(p.row)}
            disabled={busyId === p.row.id}
            sx={{ ml: "8px" }}
          >
            {p.row.status === "ACTIVE" ? "Suspend" : "Activate"}
          </IpdButton>
        </Box>
      ) },
  ];

  return (
    <Box sx={{ animation: "ipdFadeIn 0.2s ease" }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "18px" }}>
        <IpdTextField select value={payerFilter} onChange={(e) => setPayerFilter(e.target.value)} SelectProps={{ displayEmpty: true }} sx={{ minWidth: "220px" }}>
          <MenuItem value="">All corporates</MenuItem>
          {payers.map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>
          ))}
        </IpdTextField>
        <IpdTextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rate cards by code / name…"
          sx={{ flex: 1, minWidth: "200px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon name="search" size={15} color={C.textSub} />
              </InputAdornment>
            ),
          }}
        />
        <IpdButton variant="outlined" onClick={() => setShowNewCorporate(true)} startIcon={<SvgIcon name="plus" size={15} color={C.text} />}>
          New corporate
        </IpdButton>
        <IpdButton onClick={() => onOpenEditor(null, null)} startIcon={<SvgIcon name="plus" size={15} color={IPD_COLORS.white} />}>
          New rate card
        </IpdButton>
      </Box>

      {error && (
        <Box sx={{ marginBottom: "14px", padding: "10px 14px", backgroundColor: IPD_COLORS.error.bg, border: `1px solid ${IPD_COLORS.error.border}`, borderRadius: "8px", color: IPD_COLORS.error.text, fontSize: "13px" }}>
          {error}
        </Box>
      )}

      {/* Table */}
      <IpdDataGrid
        loading={loading}
        rows={visible}
        columns={columns}
        emptyMessage="No rate cards yet. Create a corporate, then add a rate card."
      />

      {showNewCorporate && (
        <NewCorporateModal
          corporates={payers}
          onClose={() => setShowNewCorporate(false)}
          onCreated={(payer) => {
            setShowNewCorporate(false);
            loadPayers();
            // Jump straight into a new rate card for the corporate just created.
            onOpenEditor(null, payer);
          }}
        />
      )}
    </Box>
  );
}
